import { useTranslation } from 'react-i18next';

import { AnimationShell, Button, Checkbox, Field, Form, Input, Link, Typography } from '@/components';
import { useState } from 'react';
import { ShowPassword } from '../show-password';
import { useValidatePassword } from '../../hooks/use-validate-password';
import { PasswordRulesList } from '../password-rules-list';

export type PasswordFormFields = {
  password: string;
  confirmed_password: string;
  accepted_ttcc: boolean;
  phone_number?: string;
};

export type PasswordFormProps = {
  readonly onSubmit: (data: PasswordFormFields) => void;
  readonly withPhone?: boolean;
};

export function PasswordForm({ onSubmit, withPhone = false }: PasswordFormProps) {
  const { t } = useTranslation(['reset-password']);
  const { i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const termsUrl =
    window.location.hostname === 'localhost' || window.location.hostname.includes('sandbox')
      ? `https://sandbox.nteaser.com/${i18n.language}/terms-and-conditions`
      : `https://nteaser.com/${i18n.language}/terms-and-conditions`;

  const privacyUrl =
    window.location.hostname === 'localhost' || window.location.hostname.includes('sandbox')
      ? `https://sandbox.nteaser.com/${i18n.language}/privacy-policy`
      : `https://nteaser.com/${i18n.language}/privacy-policy`;

  return (
    <Form<PasswordFormFields> onSubmit={onSubmit}>
      {({ watch, register, formState }) => {
        const password = watch('password') || '';
        const { hasMinLength, hasUppercase, hasLowercase, hasNumber, hasSpecialChar, isValid } =
          useValidatePassword(password);
        const [isTouched, setIsTouched] = useState(false);
        const showPasswordRules = isTouched || (!!password && !isValid);

        return (
          <div className="flex flex-col space-y-7">
            {withPhone && (
              <Field
                id="phone_number"
                label={t('Phone')}
                error={formState.errors['phone_number']}
                delay={0.1}
                classes={{ field: 'space-y-1' }}
              >
                <Input
                  type="tel"
                  placeholder="e.g. +34123456789"
                  autoComplete="tel"
                  color={formState.errors['phone_number'] ? 'error' : 'primary'}
                  registration={register('phone_number', {
                    pattern: {
                      value: /^\+?[0-9\s-]+$/,
                      message: t("Invalid format. Only numbers, spaces, dashes, and an initial '+' are allowed"),
                    },
                    maxLength: {
                      value: 20,
                      message: t('The phone number cannot have more than 20 characters'),
                    },
                  })}
                />
              </Field>
            )}
            <Field
              id="password"
              label={t('Password')}
              error={formState.errors['password']}
              delay={0.15}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                color={formState.errors['password'] ? 'error' : 'primary'}
                registration={register('password', {
                  required: t('This field is required'),
                  validate: () =>
                    (hasUppercase && hasLowercase && hasNumber && hasSpecialChar) ||
                    t('Password does not meet all requirements'),
                })}
                rightElement={<ShowPassword showPassword={showPassword} setShowPassword={setShowPassword} />}
                onFocus={() => setIsTouched(true)}
              />
            </Field>

            {showPasswordRules && (
              <PasswordRulesList
                hasMinLength={hasMinLength}
                hasUppercase={hasUppercase}
                hasLowercase={hasLowercase}
                hasNumber={hasNumber}
                hasSpecialChar={hasSpecialChar}
              />
            )}

            <Field
              id="confirmed_password"
              label={t('Repeat password')}
              error={formState.errors['confirmed_password']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                type={showRepeatPassword ? 'text' : 'password'}
                autoComplete="new-password"
                color={formState.errors['confirmed_password'] ? 'error' : 'primary'}
                registration={register('confirmed_password', {
                  required: t('This field is required'),
                  validate: (value) => value === watch('password') || t('The passwords do not match'),
                })}
                rightElement={
                  <ShowPassword showPassword={showRepeatPassword} setShowPassword={setShowRepeatPassword} />
                }
              />
            </Field>

            <Field
              id="accepted_ttcc"
              error={formState.errors['accepted_ttcc']}
              delay={0.6}
              classes={{ field: 'space-y-1' }}
            >
              <Checkbox
                id="accepted_ttcc"
                color="primary"
                registration={register('accepted_ttcc', {
                  required: t('You must accept the terms and conditions to register.'),
                })}
              >
                <Typography as="p" color="secondary" size="body-3" weight="normal">
                  {t("I read and accept nTeaser's ")}
                  <Link to={termsUrl} target="_blank">
                    {t('Terms and Conditions')}
                  </Link>
                  {t(' and ')}
                  <Link to={privacyUrl} target="_blank">
                    {t('Privacy Policy')}
                  </Link>
                  {t('.')}
                </Typography>
              </Checkbox>
            </Field>

            <AnimationShell type="opacity" delay={0.55}>
              <Button type="submit" size="lg" className="w-full">
                {t('Continue')}
              </Button>
            </AnimationShell>
          </div>
        );
      }}
    </Form>
  );
}
